import { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchStatesAsync,
  selectAllStates,
  selectStatesLoading,
  selectStatesError,
} from "../../../Slices/statesSlice";
import {
  getPaymentPlans,
  isAuthenticated,
  addSchool,
} from "../../../ApiService/ApiService";
import {
  isValidEmail,
  isValidPin,
  isValidPhoneNumber,
} from "../../../Utils/validFormDetails";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import CustomDialog from "../../../Components/CustomDialog";

function AddSchool({ fetchSchools, setShowAddSchool }) {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(fetchStatesAsync());
  }, [dispatch]);
  const states = useSelector(selectAllStates);
  const statesLoading = useSelector(selectStatesLoading);
  const statesError = useSelector(selectStatesError);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    street1: "",
    street2: "",
    city: "",
    stateId: "",
    pin: "",
    helpLine1: "",
    helpLine2: "",
    affiliation: "",
    planId: "",
    tagLine: "",
    website: "",
  });

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({
    visible: false,
    title: "",
    message: "",
    buttons: [],
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const res = await getPaymentPlans();
      setPlans(res.data.data);
    } catch (err) {
      console.error("Error fetching plans:", err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleDialogClose = () => {
    setDialog({ ...dialog, visible: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !formData.name ||
      !formData.email ||
      !formData.street1 ||
      !formData.city ||
      !formData.stateId ||
      !formData.pin ||
      !formData.helpLine1 ||
      !formData.planId
    ) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Please fill all the details",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
      return;
    }

    if (!isValidEmail(formData.email)) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Please enter a valid email address",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
      return;
    }

    if (!isValidPin(formData.pin)) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Please enter a valid 6-digit pin",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
      return;
    }

    if (!isValidPhoneNumber(formData.helpLine1)) {
      setDialog({
        visible: true,
        title: "Error",
        message:
          "Please enter a valid 10-digit help line number for Help Line 1.",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });

      return;
    }

    if (formData.helpLine2 && !isValidPhoneNumber(formData.helpLine2)) {
      setDialog({
        visible: true,
        title: "Error",
        message:
          "Please enter a valid 10-digit help line number for Help Line 2.",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
      return;
    }
    setLoading(true);
    try {
      if (isAuthenticated()) {
        const response = await addSchool(formData);
        // console.log("RES", response?.data);
        if (response) {
          if (response?.data?.status === 200) {
            setDialog({
              visible: true,
              title: "Success",
              message: "School added",
              buttons: [{ name: "Close", onPress: handleDialogClose }],
            });
            setFormData({
              name: "",
              email: "",
              street1: "",
              street2: "",
              city: "",
              stateId: "",
              pin: "",
              helpLine1: "",
              helpLine2: "",
              affiliation: "",
              planId: "",
              tagLine: "",
              website: "",
            });
            fetchSchools();
            setShowAddSchool(false);
          } else {
            const errorMessage = response?.data?.errorMessage;
            setDialog({
              visible: true,
              title: "Error",
              message: errorMessage,
              buttons: [{ name: "Close", onPress: handleDialogClose }],
            });
          }
        } else {
          setDialog({
            visible: true,
            title: "Error",
            message: "Invalid response from the server.",
            buttons: [{ name: "Close", onPress: handleDialogClose }],
          });
        }
      } else {
        setDialog({
          visible: true,
          title: "Unauthorized action",
          message: "Please login and try again",
          buttons: [{ name: "Close", onPress: handleDialogClose }],
        });
        return;
      }
    } catch (error) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Failed to add school",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CustomDialog
        visible={dialog.visible}
        title={dialog.title}
        message={dialog.message}
        buttons={dialog.buttons}
        onClose={handleDialogClose}
      />
      <Box sx={{ maxWidth: "75%", margin: "auto", padding: 3 }}>
        <Paper elevation={3} sx={{ padding: 5 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontWeight: 600, fontSize: 22, color: "black" }}
          >
            Add School
          </Typography>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="School Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Tag Line"
                  name="tagLine"
                  value={formData.tagLine}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  name="email"
                  height="20px"
                  value={formData.email}
                  onChange={handleChange}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street 1"
                  name="street1"
                  value={formData.street1}
                  onChange={handleChange}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street 2"
                  name="street2"
                  value={formData.street2}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="state" size="small">
                    State *
                  </InputLabel>
                  <Select
                    name="stateId"
                    label="State"
                    labelId="state"
                    value={formData.stateId}
                    onChange={handleChange}
                    size="small"
                    required
                  >
                    {statesLoading ? (
                      <MenuItem value="">Loading...</MenuItem>
                    ) : statesError ? (
                      <MenuItem value="">Error fetching states</MenuItem>
                    ) : (
                      states.map((state) => (
                        <MenuItem key={state.id} value={state.id}>
                          {state.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="PIN"
                  name="pin"
                  value={formData.pin}
                  onChange={handleChange}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Affiliation"
                  name="affiliation"
                  value={formData.affiliation}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Help Line 1"
                  name="helpLine1"
                  value={formData.helpLine1}
                  onChange={handleChange}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Help Line 2"
                  name="helpLine2"
                  value={formData.helpLine2}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="plans" size="small">
                    Plan *
                  </InputLabel>
                  <Select
                    name="planId"
                    labelId="plans"
                    label="Plans"
                    value={formData.planId}
                    onChange={handleChange}
                    size="small"
                    required
                  >
                    {plans &&
                      plans?.map((plan) => (
                        <MenuItem key={plan?.id} value={plan?.id}>
                          {plan?.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                {" "}
                <Button
                  fullWidth
                  variant="contained"
                  color="inherit"
                  onClick={() => setShowAddSchool(false)}
                  startIcon={<ArrowBack />}
                  sx={{ textTransform: "unset" }}
                >
                  Back
                </Button>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                {" "}
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  endIcon={<ArrowForward />}
                  sx={{ textTransform: "unset" }}
                >
                  {loading ? "Loading..." : "Submit"}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Box>
    </>
  );
}

export default AddSchool;
